// Squad movement: pathing toward moveTarget + soft separation (plan §17.2).

import { unitDef } from '../../content/registry';
import { TUNING } from '../../content/tuning';
import { dist, moveToward } from '../../shared/vec';
import { dynamicEjectionPoint, hasOpenClearance, physicalCorridorClear, physicalLineClear, yieldWaypoint } from '../mobility';
import type { Sim } from '../sim';
import { squadMoveSpeed, type HLCommand, type Squad } from '../state';
import { squadBodyRadius, wallGateRect } from './gates';

const dt = TUNING.dt;

const SEP_RADIUS: Record<string, number> = {
  small: 0.5,
  medium: 0.7,
  large: 1.3,
  giant: 2.5,
};

/** Distance over which a squad eases to a stop at its final goal. // TEST */
const ARRIVE_RADIUS = 2.6;
/** Floor so easing never becomes a crawl that never finishes. // TEST */
const ARRIVE_MIN_SPEED = 0.28;

/** Comparable remaining route length, including the gap from a partial path to its goal. */
function remainingRouteDistance(s: Squad, goal: { x: number; y: number }): number {
  let total = 0;
  let from = s.pos;
  if (s.path) {
    for (let i = s.pathIndex; i < s.path.length; i++) {
      total += dist(from, s.path[i]);
      from = s.path[i];
    }
  }
  return total + dist(from, goal);
}

function routeSignature(s: Squad, goal: { x: number; y: number }): number {
  let hash = 2166136261 >>> 0;
  const mix = (value: number): void => {
    hash ^= value | 0;
    hash = Math.imul(hash, 16777619) >>> 0;
  };
  mix(Math.round(goal.x * 2));
  mix(Math.round(goal.y * 2));
  mix(s.path ? s.path.length - s.pathIndex : -1);
  if (s.path) {
    for (let i = s.pathIndex; i < s.path.length; i++) {
      mix(Math.round(s.path[i].x * 2));
      mix(Math.round(s.path[i].y * 2));
    }
  }
  return hash;
}

function cloneCommand(command: HLCommand): HLCommand {
  return {
    ...command,
    targetPos: command.targetPos ? { ...command.targetPos } : undefined,
    pointA: command.pointA ? { ...command.pointA } : undefined,
    pointB: command.pointB ? { ...command.pointB } : undefined,
  };
}

interface PortalSteering {
  point: { x: number; y: number };
  exitsGate: boolean;
}

function routeCrossesGate(
  s: Squad,
  goal: { x: number; y: number },
  verticalWall: boolean,
  centerNormal: number,
  centerTangent: number,
  tangentHalf: number,
): boolean {
  let from = s.pos;
  const points = s.path ? [...s.path.slice(s.pathIndex), goal] : [goal];
  for (const to of points) {
    const fromNormal = (verticalWall ? from.x : from.y) - centerNormal;
    const toNormal = (verticalWall ? to.x : to.y) - centerNormal;
    if (fromNormal === 0 || toNormal === 0 || fromNormal * toNormal < 0) {
      const denom = fromNormal - toNormal;
      const t = Math.abs(denom) < 1e-9 ? 0 : fromNormal / denom;
      const fromTangent = verticalWall ? from.y : from.x;
      const toTangent = verticalWall ? to.y : to.x;
      const crossingTangent = fromTangent + (toTangent - fromTangent) * t;
      if (Math.abs(crossingTangent - centerTangent) <= tangentHalf + 1) return true;
    }
    from = to;
  }
  return false;
}

/** Center large formations through a gate instead of following an edge A* cell. */
function gatePortalSteering(sim: Sim, s: Squad, goal: { x: number; y: number }): PortalSteering | null {
  const bodyRadius = squadBodyRadius(s);
  if (bodyRadius < 1.25) return null;
  let best: PortalSteering | null = null;
  let bestDistance = Infinity;
  let bestId = Infinity;

  for (const wall of sim.state.buildings.values()) {
    if (wall.state !== 'active' || !wall.navStamped) continue;
    const rect = wallGateRect(wall);
    if (!rect) continue;
    const verticalWall = rect.y1 - rect.y0 >= rect.x1 - rect.x0;
    const centerNormal = verticalWall ? wall.pos.x : wall.pos.y;
    const centerTangent = verticalWall ? (rect.y0 + rect.y1) / 2 : (rect.x0 + rect.x1) / 2;
    const posNormal = verticalWall ? s.pos.x : s.pos.y;
    const posTangent = verticalWall ? s.pos.y : s.pos.x;
    const goalNormal = verticalWall ? goal.x : goal.y;
    const normalHalf = verticalWall ? (rect.x1 - rect.x0) / 2 : (rect.y1 - rect.y0) / 2;
    const tangentHalf = verticalWall ? (rect.y1 - rect.y0) / 2 : (rect.x1 - rect.x0) / 2;
    if (!routeCrossesGate(s, goal, verticalWall, centerNormal, centerTangent, tangentHalf)) continue;
    const centerClearance = normalHalf + bodyRadius + 0.45;
    if (Math.abs(posNormal - centerNormal) > centerClearance + 9) continue;
    if (Math.abs(posTangent - centerTangent) > tangentHalf + 8) continue;

    const goalDelta = goalNormal - centerNormal;
    if (Math.abs(goalDelta) < 0.5) continue;
    let entrySide = posNormal < centerNormal ? -1 : 1;
    // While the body overlaps the portal corridor, keep its entry side opposite the goal;
    // otherwise crossing the center flips the sign early and releases edge steering.
    if (Math.abs(posNormal - centerNormal) <= centerClearance + 0.75) {
      entrySide = goalDelta > 0 ? -1 : 1;
    }
    if (entrySide * goalDelta >= -0.5) continue;

    const entryNormal = centerNormal + entrySide * centerClearance;
    const exitNormal = centerNormal - entrySide * centerClearance;
    const entry = verticalWall
      ? { x: entryNormal, y: centerTangent }
      : { x: centerTangent, y: entryNormal };
    const exit = verticalWall
      ? { x: exitNormal, y: centerTangent }
      : { x: centerTangent, y: exitNormal };
    if (!sim.nav.isWalkable(entry, s.owner) || !sim.nav.isWalkable(exit, s.owner)) continue;
    const enteredCorridor = (posNormal - entryNormal) * -entrySide > 0.2;
    const alignedAtEntry = dist(s.pos, entry) <= 0.65 || enteredCorridor;
    if (!alignedAtEntry && !physicalLineClear(sim, s.pos, entry, s.owner)) continue;
    const steering = { point: alignedAtEntry ? exit : entry, exitsGate: alignedAtEntry };
    const gateDistance = Math.hypot(posNormal - centerNormal, posTangent - centerTangent);
    if (gateDistance < bestDistance - 1e-9 || (Math.abs(gateDistance - bestDistance) <= 1e-9 && wall.id < bestId)) {
      best = steering;
      bestDistance = gateDistance;
      bestId = wall.id;
    }
  }
  return best;
}

/** Nearest owned, currently reachable fallback for a withdrawal cut off by an enemy wall. */
function withdrawFallback(sim: Sim, s: Squad, intended: { x: number; y: number }): { x: number; y: number } {
  let best = { ...s.pos };
  let bestScore = Infinity;
  const consider = (raw: { x: number; y: number }): void => {
    const candidate = sim.nav.nearestWalkable(raw, 12, s.owner, s.pos);
    if (!sim.nav.canReach(s.pos, candidate, s.owner)) return;
    const score = dist(candidate, intended) + dist(s.pos, candidate) * 0.1;
    if (score < bestScore) {
      best = candidate;
      bestScore = score;
    }
  };
  consider(s.lastOpenPos);
  for (const site of sim.state.sites) if (site.owner === s.owner) consider(site.pos);
  for (const building of sim.state.buildings.values()) {
    if (building.owner === s.owner && building.state === 'active') consider(building.rally);
  }
  return best;
}

export function stepMovement(sim: Sim): void {
  const st = sim.state;
  const squads = [...st.squads.values()];

  for (const s of squads) {
    if (s.mountedOnWall !== null) continue; // garrisoned: fixed on the battlements
    if (s.mountedOnSquad !== null) continue; // riding a host: position slaved by mounting.ts
    if (hasOpenClearance(sim, s.pos, s.owner)) s.lastOpenPos = { ...s.pos };

    // active charge dash: forced move straight at the victim, impact on contact
    if (s.charge !== null) {
      const c = s.charge;
      const victim = st.squads.get(c.targetId);
      c.remaining -= dt;
      if (
        !victim ||
        victim.dead ||
        c.remaining <= 0 ||
        !physicalCorridorClear(sim, s.pos, victim.pos, s.owner, squadBodyRadius(s))
      ) {
        s.charge = null;
        s.path = null;
        s.pathGoal = null;
        s.repathCooldown = 0;
      } else {
        const speed = squadMoveSpeed(s) * c.speedMult;
        const np = moveToward(s.pos, victim.pos, speed * dt);
        if (!sim.nav.isWalkable(np, s.owner)) {
          // A gate can close after the telegraph. Abort immediately instead of spending the
          // rest of the dash rotating in place against its footprint.
          s.charge = null;
          s.path = null;
          s.pathGoal = null;
          s.repathCooldown = 0;
        } else {
          s.pos = np;
          s.facing = Math.atan2(victim.pos.y - s.pos.y, victim.pos.x - s.pos.x);
          if (dist(s.pos, victim.pos) <= 2.4) {
            if (c.impactDamage > 0) {
              sim.damageSquad(victim, c.impactDamage, { isRanged: false, attackerOwner: s.owner });
            }
            if (c.impactSlowSeconds > 0 && c.impactSlowMult < 1 && !victim.dead) {
              victim.buffs.push({ stat: 'moveSpeed', mult: c.impactSlowMult, remaining: c.impactSlowSeconds });
            }
            if (c.impactKnockback > 0 && !victim.dead) {
              const d = Math.max(0.001, dist(s.pos, victim.pos));
              sim.teleportSquad(
                victim,
                {
                  x: victim.pos.x + ((victim.pos.x - s.pos.x) / d) * c.impactKnockback,
                  y: victim.pos.y + ((victim.pos.y - s.pos.y) / d) * c.impactKnockback,
                },
                c.impactKnockback,
              );
            }
            if (c.impactKnockdownSeconds > 0 && !victim.dead) {
              sim.knockdownSquad(victim, c.impactKnockdownSeconds);
            }
            s.charge = null;
          }
          continue; // the dash overrides normal steering this tick
        }
      }
    }

    if (s.repathCooldown > 0) s.repathCooldown -= dt;
    const goal = s.moveTarget;
    if (!goal) {
      s.path = null;
      s.pathGoal = null;
      s.stuckTime = 0;
      continue;
    }
    if (dist(s.pos, goal) < 0.4) {
      s.stuckTime = 0;
      continue;
    }

    // (re)path when goal moved or no path
    const goalChanged = !!s.pathGoal && dist(s.pathGoal, goal) > 2.5;
    const pathExhausted = !!s.path && s.pathIndex >= s.path.length;
    const navigationChanged = pathExhausted && s.pathNavRevision !== sim.nav.revision;
    const needsPath =
      !s.path ||
      !s.pathGoal ||
      goalChanged ||
      // A partial path intentionally ends at the reachable frontier. Do not flood A*
      // there every 0.5 s. Any obstacle revision may expose a nearer second frontier;
      // final connectivity becoming reachable also wakes the route.
      (pathExhausted &&
        (navigationChanged || sim.nav.canReach(s.pos, goal, s.owner)));
    if (needsPath && s.repathCooldown <= 0) {
      // straight-line shortcut when visible (owner-aware: enemy gates block us, own gates don't)
      if (sim.nav.lineOfSight(s.pos, goal, s.owner)) {
        s.path = [{ ...goal }];
      } else {
        s.path = sim.nav.findPath(s.pos, goal, s.owner, true);
      }
      s.pathIndex = 0;
      s.pathGoal = { ...goal };
      s.pathNavRevision = sim.nav.revision;
      s.repathCooldown = 0.5;
      if (!s.path) continue;
    }
    if (!s.path) continue;

    // string-pulling: skip ahead to the furthest visible waypoint (removes grid zig-zag)
    while (
      s.pathIndex + 1 < s.path.length &&
      sim.nav.lineOfSight(s.pos, s.path[s.pathIndex + 1], s.owner)
    ) {
      s.pathIndex++;
    }

    const portal = gatePortalSteering(sim, s, goal);
    const wp = portal?.point ?? s.path[s.pathIndex];
    if (!wp) continue;
    let speed = squadMoveSpeed(s);
    // Arrival easing. Squads used to run at full speed until they were within 0.4 m of the goal
    // and then stop dead, which reads as a hard snap — especially for a group all arriving at
    // once. Ramp down over the last few metres instead.
    //
    // Only when actually approaching the final goal: braking on an intermediate path waypoint
    // would make every corner slow, and only when not engaged, so combat approaches keep their
    // urgency.
    if (!s.engaged) {
      const toGoal = dist(s.pos, goal);
      if (toGoal < ARRIVE_RADIUS) {
        speed *= Math.max(ARRIVE_MIN_SPEED, toGoal / ARRIVE_RADIUS);
      }
    }
    const next = moveToward(s.pos, wp, speed * dt);
    if (!sim.nav.isWalkable(next, s.owner)) {
      // A blocker may have appeared after this route was made. Do not step into it and then
      // visibly snap back out; discard the stale route and repath from safe ground next tick.
      s.path = null;
      s.pathGoal = null;
      s.repathCooldown = 0;
      continue;
    }
    s.pos = next;
    if (dist(s.pos, wp) < 0.5) {
      if (portal?.exitsGate) {
        // The old partial path may end at the entry-side frontier. Replan cleanly once the
        // formation has cleared the far leaf instead of steering it back into the doorway.
        s.path = null;
        s.pathGoal = null;
        s.repathCooldown = 0;
      } else if (!portal) {
        s.pathIndex++;
      }
    }
  }

  // soft separation (O(n²) is fine at ≤60 squads). Pushes accumulate per squad and are
  // clamped per tick — instant multi-neighbor shoves in blob fights read as jolting.
  const mobile = squads.filter((s) => s.mountedOnWall === null && s.mountedOnSquad === null);
  // fighters plant (AoE-style): squads in/just out of combat receive no crowd shove, so
  // melee reads as a held line instead of sliding; bystanders still flow around them
  const planted = mobile.map(
    (s) => s.engaged || (s.targetId !== null && st.time - s.lastCombatAt < TUNING.combatPlantSeconds),
  );
  const sepX = new Float64Array(mobile.length);
  const sepY = new Float64Array(mobile.length);
  for (let i = 0; i < mobile.length; i++) {
    const a = mobile[i];
    const ra = SEP_RADIUS[unitDef(a.defId).sizeTag] ?? 0.7;
    for (let j = i + 1; j < mobile.length; j++) {
      const b = mobile[j];
      const rb = SEP_RADIUS[unitDef(b.defId).sizeTag] ?? 0.7;
      const minD = ra + rb;
      const dx = b.pos.x - a.pos.x;
      const dy = b.pos.y - a.pos.y;
      const d2 = dx * dx + dy * dy;
      if (d2 >= minD * minD) continue;
      // Squads can spawn/teleport at exactly the same point. A zero-length direction used
      // to skip separation forever; derive a stable pair direction instead of using RNG.
      const overlapSeed = ((a.id * 73856093) ^ (b.id * 19349663)) >>> 0;
      const overlapAngle = ((overlapSeed % 360) / 360) * Math.PI * 2;
      const d = d2 < 1e-9 ? 0 : Math.sqrt(d2);
      const push = (minD - d) * 0.5;
      const nx = d === 0 ? Math.cos(overlapAngle) : dx / d;
      const ny = d === 0 ? Math.sin(overlapAngle) : dy / d;
      // Fortress Stride flavor: giants push, are not pushed
      const aGiant = unitDef(a.defId).sizeTag === 'giant';
      const bGiant = unitDef(b.defId).sizeTag === 'giant';
      let aPush = bGiant && !aGiant ? push * 2 : aGiant && !bGiant ? 0 : push;
      let bPush = aGiant && !bGiant ? push * 2 : bGiant && !aGiant ? 0 : push;
      // Combat planting keeps ordinary lines stable, but never lets infantry physically
      // pin a giant: Fortress Stride still parts the crowd around an engaged Colossus.
      if (planted[i] && !(bGiant && !aGiant)) aPush = 0;
      if (planted[j] && !(aGiant && !bGiant)) bPush = 0;
      // Symmetric separation exactly cancels both forward steps when equal-size squads meet
      // head-on in a choke. Stable id-based right-of-way lets one file through while the
      // other yields; giant-vs-infantry keeps Fortress Stride precedence above.
      if (aGiant === bGiant && a.moveTarget && b.moveTarget) {
        const adx = a.moveTarget.x - a.pos.x;
        const ady = a.moveTarget.y - a.pos.y;
        const bdx = b.moveTarget.x - b.pos.x;
        const bdy = b.moveTarget.y - b.pos.y;
        const al = Math.hypot(adx, ady);
        const bl = Math.hypot(bdx, bdy);
        const headOn = al > 0.1 && bl > 0.1 && (adx * bdx + ady * bdy) / (al * bl) < -0.5;
        if (headOn) {
          if (a.id < b.id) {
            aPush = 0;
            bPush = Math.max(bPush, push * 2);
          } else {
            bPush = 0;
            aPush = Math.max(aPush, push * 2);
          }
        }
      }
      sepX[i] -= nx * aPush;
      sepY[i] -= ny * aPush;
      sepX[j] += nx * bPush;
      sepY[j] += ny * bPush;
    }
  }
  for (let i = 0; i < mobile.length; i++) {
    let px = sepX[i];
    let py = sepY[i];
    const mag = Math.hypot(px, py);
    if (mag < 1e-9) continue;
    const s = mobile[i];
    // Crowd displacement was clamped to a flat 2.5 m/s — below the 4.2-4.4 m/s top speed of
    // several units, so in a dense blob separation could not keep up with movement and squads
    // visibly interpenetrated. Scale the clamp with the squad's own speed so faster units can
    // also push apart faster, keeping the flat floor for slow/stationary ones.
    //
    // Deliberately computed here in the clamp loop, not in the accumulation loop above: the
    // sepX/sepY Float64Array sums must keep their exact pair-visit order, and this only
    // rescales the finished vector.
    const maxSep = Math.max(2.5, squadMoveSpeed(s) * 0.75) * dt; // TEST
    if (mag > maxSep) {
      px = (px / mag) * maxSep;
      py = (py / mag) * maxSep;
    }
    const np = { x: s.pos.x + px, y: s.pos.y + py };
    if (sim.nav.isWalkable(np, s.owner)) s.pos = np;
  }

  // Dynamic blocker invariant: a wall/gate that appears over a squad may only eject it toward
  // the last open side. Permanent terrain never authorizes a recovery snap.
  for (const s of mobile) {
    if (!sim.nav.isWalkable(s.pos, s.owner)) {
      const exit = dynamicEjectionPoint(sim, s.pos, s.lastOpenPos, s.owner);
      if (exit) {
        s.pos = exit;
        s.prevPos = { ...exit };
        s.path = null;
        s.pathGoal = null;
        s.repathCooldown = 0;
        s.stuckTime = 0;
        s.progressGoal = null;
        s.bestRouteRemaining = Infinity;
        s.progressNavRevision = sim.nav.revision;
        s.progressRouteSignature = 0;
      }
      continue;
    }
    if (hasOpenClearance(sim, s.pos, s.owner)) s.lastOpenPos = { ...s.pos };

    const goal = s.moveTarget;
    const speed = squadMoveSpeed(s);
    if (!goal || dist(s.pos, goal) < 0.4 || speed <= 0.01 || s.engaged) {
      // The formation march is over: the squad has arrived, has nowhere to go, or is fighting.
      // Holding the group cap past this point would leave fast units permanently slowed.
      s.groupSpeedCap = null;
      s.stuckTime = 0;
      s.progressGoal = null;
      s.bestRouteRemaining = Infinity;
      s.progressNavRevision = sim.nav.revision;
      s.progressRouteSignature = 0;
      continue;
    }

    const signature = routeSignature(s, goal);
    const newGoal = !s.progressGoal || dist(s.progressGoal, goal) > 1;
    const materialNavChange =
      s.progressNavRevision !== sim.nav.revision &&
      s.progressRouteSignature !== 0 &&
      s.progressRouteSignature !== signature;
    if (newGoal || materialNavChange) {
      s.progressGoal = { ...goal };
      s.bestRouteRemaining = Infinity;
      s.stuckTime = 0;
    }
    s.progressNavRevision = sim.nav.revision;
    s.progressRouteSignature = signature;

    // Measure monotonic remaining-route progress after separation. A per-tick displacement
    // check can be kept alive by lateral oscillation; a best-so-far route metric cannot.
    const expected = speed * dt;
    const remaining = remainingRouteDistance(s, goal);
    const progressThreshold = Math.max(0.01, expected * 0.08);
    const routeProgressed = remaining <= s.bestRouteRemaining - progressThreshold;
    if (remaining < s.bestRouteRemaining) s.bestRouteRemaining = remaining;
    if (routeProgressed) {
      s.stuckTime = Math.max(0, s.stuckTime - dt * 3);
      continue;
    }
    s.stuckTime += dt;
    if (s.stuckTime < 1.0) continue;

    s.path = null;
    s.pathGoal = null;
    s.repathCooldown = 0;
    // Only immutable terrain may legitimately halt an order. If a destructible enemy wall
    // divides the physical route, a combat squad breaches it via attack-move and resumes.
    if (!sim.nav.canReachTerrain(s.pos, goal)) {
      s.stuckTime = 0;
      continue;
    }
    let recoveryGoal = goal;
    if (!sim.nav.canReach(s.pos, goal, s.owner)) {
      if (s.command.kind === 'withdraw') {
        // Withdrawal never attacks its way through a hostile door. Fall back to the safest
        // owned anchor in the current component and hold until given another order.
        recoveryGoal = withdrawFallback(sim, s, goal);
        // A plain move preserves withdrawal's no-retaliation intent. Auto-defend would
        // generate a 6 m guard slot that can fall across the very wall we refused to breach.
        s.command = { kind: 'move', targetPos: { ...recoveryGoal } };
        s.moveTarget = recoveryGoal;
        s.breachResume = null;
      } else if (s.command.kind !== 'attack') {
        // Capture/defend/patrol/raid/escort/move all preserve their exact intent while a
        // temporary attack-move acquires and destroys the reachable wall segment.
        if (!s.breachResume) {
          s.breachResume = { command: cloneCommand(s.command), goal: { ...goal } };
        }
        s.command = { kind: 'attack', targetPos: { ...goal } };
      }
      s.targetId = null;
      s.engaged = false;
    }

    const portalRecovery = gatePortalSteering(sim, s, recoveryGoal);
    const yieldAt = portalRecovery?.point ?? yieldWaypoint(sim, s.id, s.pos, recoveryGoal, s.owner);
    if (yieldAt) {
      s.path = [yieldAt];
      s.pathIndex = 0;
      s.pathGoal = { ...recoveryGoal };
      s.pathNavRevision = sim.nav.revision;
      s.repathCooldown = 0.2;
    }
    // Repeated failures retry quickly; a successful side-step drains this naturally.
    s.stuckTime = 0.35;
  }
}
